import { useEffect, useMemo, useRef, useState } from 'react'
import {
  X, Loader2, Check, Pencil, Plus, Trash2, GripVertical, ChevronUp, ChevronDown,
  Power, AlertTriangle, Tag, Layers, BadgePercent, Copy,
} from 'lucide-react'
import { postJson, patchJson } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { canEditPromotion } from '../../lib/role.js'
import SearchableSelect from '../SearchableSelect.jsx'
import Combobox from '../Combobox.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'
import {
  activeInfo, readPattern, normalizePattern, patternMax, patternTotal,
  maxMismatch, emptyStep, dfMode, dfPercentHint, DF_FIXED, DF_PERCENT,
  indexBillingTypes, billingTypeOptions, btLabel, baht,
} from '../../lib/promotion.js'

const INPUT = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 disabled:bg-slate-50 disabled:text-slate-400'
const NUM = `${INPUT} tabular-nums text-right`

/**
 * One promotion — detail + edit drawer, opened from Manager › ตั้งค่าโปรโมชั่น.
 * Also the create form (`create` prop): same fields, POST instead of PATCH.
 *
 * The list already carries the whole record (Xano returns every column), so unlike
 * DentistDrawer there is no second fetch — the row IS the record.
 *
 * Roles: GM / Admin write, everyone else who can reach the page reads. The proxy and Xano
 * both re-check; this only shapes the UI.
 *
 * Saving is split the same way as the dentist drawer: สถานะ (active) is a switch that
 * applies on the spot, everything else is a form with one save.
 */
export default function PromotionDrawer({ row, billingTypes = [], types, create = false, onClose, onSaved }) {
  const me = useMe()
  const canEdit = canEditPromotion(me)

  const [rec, setRec] = useState(row || null)
  const [editing, setEditing] = useState(create)
  const [form, setForm] = useState(() => (create ? blankForm() : null))
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState('')
  const [confirmPattern, setConfirmPattern] = useState(false)

  useEffect(() => { if (!create) setRec(row || null) }, [row, create])

  const p = rec || {}
  const a = activeInfo(p.active === true)
  const btById = useMemo(() => indexBillingTypes(billingTypes), [billingTypes])

  // The stored pattern as EDITABLE rows: sorted into true charging order, each step's count
  // read from the repeat_acc gap rather than the stored `repeat` — see readPattern().
  const storedEditable = useMemo(() => readPattern(p), [p])
  // The DRAFT itself stays raw — whatever is in the number boxes, mid-typing. Normalising on
  // every keystroke would fight the user: clearing a field to retype it would snap straight
  // back to 1 (repeat) or 0 (price). It is normalised here for the derived numbers, for the
  // changed-check, and once more on save.
  const draftSteps = useMemo(() => normalizePattern(form?.pattern || []), [form])
  const mismatch = create ? null : maxMismatch(p)

  function startEdit() {
    setForm({
      Name: p.Name || '',
      Maintype: p.Maintype || '',
      Subtype: p.Subtype || '',
      Detail: p.Detail || '',
      commission: p.commission === true,
      commission_price: Number(p.commission_price) || 0,
      recurring: p.recurring === true,
      waive_cc: p.waive_cc === true,
      active: p.active === true,
      pattern: storedEditable.map((s) => ({ ...s })),
    })
    setSaveErr('')
    setEditing(true)
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  // Reordering or re-pricing a pattern changes what the NEXT visit bills for every patient
  // already mid-course on this promotion — the pattern lives on the promotion, not on their
  // promotion_patient row. Worth a confirm, but only when the pattern actually moved.
  const patternChanged = create
    || JSON.stringify(draftSteps) !== JSON.stringify(normalizePattern(storedEditable))

  // The retroactive warning is about patients already mid-course, so it has nothing to say
  // about a promotion that does not exist yet.
  function attemptSave() {
    if (patternChanged && !create) setConfirmPattern(true)
    else save()
  }

  async function save() {
    setConfirmPattern(false)
    if (!form) return
    const name = String(form.Name || '').trim()
    if (!name) { setSaveErr('ต้องมีชื่อโปรโมชั่น'); return }

    setSaving(true); setSaveErr('')
    const body = {
      Name: name,
      Maintype: String(form.Maintype || '').trim(),
      Subtype: String(form.Subtype || '').trim(),
      Detail: String(form.Detail || '').trim(),
      commission: !!form.commission,
      commission_price: form.commission ? Number(form.commission_price) || 0 : 0,
      recurring: !!form.recurring,
      waive_cc: !!form.waive_cc,
    }
    // `pattern` is sent ONLY when it moved. Xano leaves pattern + max alone when the key is
    // absent, so renaming a promotion cannot rewrite its billing schedule — which matters
    // because the 23 rows with a drifted `repeat`, and the three broken ones, would
    // otherwise be quietly rewritten by an edit that had nothing to do with them.
    // `step` / `repeat_acc` are stripped: Xano re-derives both from this array's ORDER.
    if (patternChanged) {
      body.pattern = normalizePattern(form.pattern).map(({ repeat, billing_type_id, price, fixedDF }) => ({
        repeat, billing_type_id, price, fixedDF,
      }))
    }
    if (create) body.active = !!form.active

    try {
      const saved = create
        ? await postJson('/api/promotion-manage', body)
        : await patchJson(`/api/promotion-manage/${p.id}`, body)
      setRec(saved)
      setEditing(false)
      setForm(null)
      onSaved?.(saved)
    } catch (e) {
      setSaveErr(String(e.message || e))
    } finally {
      setSaving(false)
    }
  }

  // The active switch writes on its own, so it works without entering edit mode.
  async function patchActive(next) {
    const saved = await patchJson(`/api/promotion-manage/${p.id}`, { active: next })
    setRec(saved)
    onSaved?.(saved)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={editing ? undefined : onClose} />
      <div className="relative w-full max-w-[600px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">

        {/* Header */}
        <div className="px-5 py-3.5 bg-white border-b border-slate-200 flex items-start gap-3">
          <span className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
            <Tag size={17} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900 leading-tight truncate">
              {create ? 'เพิ่มโปรโมชั่นใหม่' : (p.Name || '(ไม่มีชื่อ)')}
            </div>
            <div className="text-[12px] text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
              {!create && <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${a.tone}`}>{a.label}</span>}
              {[p.Maintype, p.Subtype].filter(Boolean).join(' · ') || (create ? 'ยังไม่ได้บันทึก' : 'ไม่ระบุประเภท')}
              {!create && <span>· #{p.id}</span>}
            </div>
          </div>
          {canEdit && !editing && !create && (
            <button
              onClick={startEdit}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px] font-medium text-slate-600 hover:border-brand-300 hover:text-brand-700"
            >
              <Pencil size={13} /> แก้ไข
            </button>
          )}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {/* ── สถานะการใช้งาน — its own card because retiring a promotion is the one
                action here that changes what the counter can sell today. ── */}
          {!create && <ActiveCard promotion={rec} canEdit={canEdit} onPatch={patchActive} />}

          {mismatch && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800 flex gap-2">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <div>
                <b>ตัวเลข max ไม่ตรงกับ pattern</b> — บันทึกไว้ {mismatch.stored} แต่ pattern คำนวณได้ {mismatch.computed} ครั้ง
                ใบเสร็จจะพิมพ์ตัวหารผิด (เช่น “3/{mismatch.stored}”) จนกว่าจะกดบันทึก pattern ใหม่อีกครั้ง
              </div>
            </div>
          )}

          {/* ── ข้อมูลทั่วไป ── */}
          <Section icon={Tag} title="ข้อมูลทั่วไป">
            {editing ? (
              <>
                <Field label="ชื่อโปรโมชั่น" hint="ใช้พิมพ์บนใบเสร็จ">
                  <input className={INPUT} value={form.Name} onChange={(e) => set('Name', e.target.value)} placeholder="เช่น Dental SPA - Revive I" />
                </Field>
                {/* Both columns are free text in Xano with no enum behind them, so the
                    picker lists what the catalog already uses and still lets a new value be
                    typed — `allowCreate` makes that explicit with a "＋ เพิ่ม" row instead of
                    silently accepting it. หมวดย่อย narrows to the chosen ประเภท. */}
                <Row2>
                  <Field label="ประเภท (Maintype)">
                    <Combobox
                      white
                      dense
                      freeText
                      allowCreate
                      value={form.Maintype}
                      onChange={(v) => set('Maintype', v)}
                      options={types?.mains || []}
                      placeholder="เลือก / พิมพ์ประเภทใหม่"
                      createLabel={(q) => `เพิ่มประเภทใหม่ “${q}”`}
                    />
                  </Field>
                  <Field label="หมวดย่อย (Subtype)">
                    <Combobox
                      white
                      dense
                      freeText
                      allowCreate
                      value={form.Subtype}
                      onChange={(v) => set('Subtype', v)}
                      options={types?.subsFor?.(form.Maintype) || types?.subs || []}
                      placeholder="เลือก / พิมพ์หมวดย่อยใหม่"
                      createLabel={(q) => `เพิ่มหมวดย่อยใหม่ “${q}”`}
                    />
                  </Field>
                </Row2>
                <Field label="รายละเอียด">
                  <textarea
                    className={`${INPUT} min-h-[76px] resize-y`}
                    value={form.Detail}
                    onChange={(e) => set('Detail', e.target.value)}
                    placeholder="รายละเอียดที่พนักงานหน้าเคาน์เตอร์เห็นตอนเสนอขาย"
                  />
                </Field>
              </>
            ) : (
              <>
                <Line k="ชื่อ">{p.Name || '—'}</Line>
                <Line k="ประเภท">{p.Maintype || '—'}</Line>
                <Line k="หมวดย่อย">{p.Subtype || '—'}</Line>
                <div className="pt-1">
                  <div className="text-[12px] text-slate-400 mb-1">รายละเอียด</div>
                  {p.Detail
                    ? <div className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">{p.Detail}</div>
                    : <span className="text-[13px] text-slate-400">—</span>}
                </div>
              </>
            )}
          </Section>

          {/* ── รูปแบบการเก็บเงิน ── */}
          <PatternSection
            rows={editing ? (form?.pattern || []) : storedEditable}
            editing={editing}
            btById={btById}
            billingTypes={billingTypes}
            onChange={(next) => set('pattern', next)}
          />

          {/* ── คอมมิชชั่น / เงื่อนไข ── */}
          <Section icon={BadgePercent} title="คอมมิชชั่น และเงื่อนไข">
            {editing ? (
              <>
                <Check2
                  on={form.commission}
                  onToggle={() => set('commission', !form.commission)}
                  label="จ่ายคอมมิชชั่นให้พนักงานผู้ขาย"
                  hint="สร้างแถวใน commission ให้ผู้ขาย ตอนโปรฯ ถูกนำไปออกบิลครั้งแรก"
                />
                {form.commission && (
                  <Field label="คอมมิชชั่นต่อการขาย 1 ครั้ง (บาท)">
                    <input
                      type="number" min="0" step="1" className={NUM}
                      value={form.commission_price}
                      onChange={(e) => set('commission_price', e.target.value)}
                    />
                  </Field>
                )}
                <Check2 on={form.recurring} onToggle={() => set('recurring', !form.recurring)} label="เป็นโปรฯ แบบต่อเนื่อง (recurring)" />
                <Check2 on={form.waive_cc} onToggle={() => set('waive_cc', !form.waive_cc)} label="ยกเว้นค่าธรรมเนียมบัตรเครดิต (waive CC)" />
                {create && (
                  <Check2 on={form.active} onToggle={() => set('active', !form.active)} label="เปิดใช้งานทันทีหลังบันทึก" />
                )}
              </>
            ) : (
              <>
                <Line k="คอมมิชชั่น">
                  {p.commission ? `฿${baht(p.commission_price)} ต่อการขาย` : 'ไม่จ่าย'}
                </Line>
                <Line k="ต่อเนื่อง">{p.recurring ? 'ใช่' : 'ไม่'}</Line>
                <Line k="ยกเว้นค่าบัตร">{p.waive_cc ? 'ใช่' : 'ไม่'}</Line>
              </>
            )}
          </Section>
        </div>

        {/* Sticky save bar */}
        {editing && (
          <div className="border-t border-slate-200 bg-white px-5 py-3 space-y-2">
            {saveErr && <div className="text-[12.5px] text-rose-600">บันทึกไม่สำเร็จ · {saveErr}</div>}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { if (create) onClose?.(); else { setEditing(false); setForm(null); setSaveErr('') } }}
                disabled={saving}
                className="rounded-lg border border-slate-200 px-3.5 py-2 text-[13px] text-slate-600 hover:bg-slate-50"
              >ยกเลิก</button>
              <button
                onClick={attemptSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {create ? 'สร้างโปรโมชั่น' : 'บันทึก'}
              </button>
            </div>
          </div>
        )}
      </div>

      {confirmPattern && (
        <ConfirmDialog
          title="แก้ไข pattern มีผลย้อนหลัง"
          message={`คนไข้ที่กำลังใช้โปรฯ นี้อยู่ยังไม่จบคอร์ส จะถูกคิดเงินตามลำดับใหม่นี้ตั้งแต่ครั้งถัดไป (ครั้งที่ออกบิลไปแล้วไม่เปลี่ยน) · รวมทั้งหมด ${patternMax(form?.pattern || [])} ครั้ง`}
          confirmLabel="บันทึก pattern ใหม่"
          tone="danger"
          onConfirm={save}
          onCancel={() => setConfirmPattern(false)}
        />
      )}
    </div>
  )
}

function blankForm() {
  return {
    Name: '', Maintype: '', Subtype: '', Detail: '',
    commission: false, commission_price: 0, recurring: false, waive_cc: false,
    active: true, pattern: [emptyStep()],
  }
}

/* ── สถานะการใช้งาน ─────────────────────────────────────────────────────────
 * A retired promotion vanishes from the counter's upsell picker (Xano's GET /promotion
 * filters active == true), so this is the switch that decides what can be sold. Patients
 * already on the promotion are NOT affected — promotion_patient/add reads the promotion
 * by id and never checks `active` — which is exactly why the confirm says so.        */
function ActiveCard({ promotion, canEdit, onPatch }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [asking, setAsking] = useState(false)
  const on = promotion?.active === true

  function toggle() {
    if (on) setAsking(true)
    else apply(true)
  }

  async function apply(next) {
    setAsking(false)
    setBusy(true); setErr('')
    try { await onPatch(next) }
    catch (e) { setErr(String(e.message || e)) }
    finally { setBusy(false) }
  }

  return (
    <section className={`rounded-xl border p-4 ${on ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-300 bg-amber-50'}`}>
      <div className="flex items-center gap-3">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${on ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
          {on ? <Power size={17} /> : <AlertTriangle size={17} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className={`text-[14.5px] font-semibold ${on ? 'text-emerald-800' : 'text-amber-800'}`}>
            {on ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
          </div>
          <div className={`text-[12.5px] leading-relaxed ${on ? 'text-emerald-700/80' : 'text-amber-700/90'}`}>
            {on
              ? 'พนักงานเลือกเสนอโปรฯ นี้ให้คนไข้ได้จากหน้าประชุมเช้า'
              : 'ไม่แสดงในรายการเสนอขายแล้ว · คนไข้ที่ซื้อไปก่อนหน้ายังใช้ต่อได้ตามปกติ'}
          </div>
          {err && <div className="text-[12px] text-rose-600 mt-1">{err}</div>}
        </div>
        {canEdit && (
          <button
            onClick={toggle}
            disabled={busy}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-medium shrink-0 disabled:opacity-40 ${
              on ? 'border border-amber-300 bg-white text-amber-700 hover:bg-amber-50'
                 : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Power size={13} />}
            {on ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
          </button>
        )}
      </div>

      {asking && (
        <ConfirmDialog
          title="ปิดใช้งานโปรโมชั่นนี้?"
          message={`“${promotion?.Name || ''}” จะหายไปจากรายการเสนอขายภายใน 5 นาที · คนไข้ที่ซื้อไปแล้วยังเก็บเงินตามคอร์สเดิมได้ตามปกติ`}
          confirmLabel="ปิดใช้งาน"
          tone="danger"
          onConfirm={() => apply(false)}
          onCancel={() => setAsking(false)}
        />
      )}
    </section>
  )
}

/* ── รูปแบบการเก็บเงิน (pattern) ─────────────────────────────────────────────
 * The ordered list of charges. Order IS the meaning: `promotion_patient/add` counts the
 * billing rows already raised for this patient and takes the first step whose cumulative
 * `repeat_acc` exceeds that count. So step 1 is the first visit's charge, and the last
 * step's repeat_acc is the "N" printed as "n/N" on the receipt.
 *
 * step / repeat_acc are shown but never typed — they are recomputed live from the row
 * order here, and again authoritatively in Xano on save.                              */
function PatternSection({ rows, editing, btById, billingTypes, onChange }) {
  // `rows` is raw while editing and readPattern() output in view mode; either way `derived`
  // is the authoritative step / repeat_acc. In view mode it reproduces what is stored (221
  // of 224 exactly, and repairs the three broken ones, which maxMismatch flags separately);
  // in edit mode it is what saving would write.
  const derived = useMemo(() => normalizePattern(rows), [rows])

  const total = patternTotal(rows)
  const max = derived.length ? derived[derived.length - 1].repeat_acc : 0

  // Every billing type, active and retired alike, grouped by workflow bucket. Same list for
  // every row, so it is built once here.
  const options = useMemo(() => billingTypeOptions(billingTypes), [billingTypes])

  function move(from, to) {
    if (to < 0 || to >= rows.length || from === to) return
    const next = [...rows]
    const [r] = next.splice(from, 1)
    next.splice(to, 0, r)
    onChange(next)
  }
  const patch = (i, k, v) => onChange(rows.map((s, j) => (j === i ? { ...s, [k]: v } : s)))

  // Reordering is previewed by sliding the cards, so the drag has to be owned by the list
  // rather than by each card: `{ from, to, pitch }`, where pitch is one slot (card height +
  // the 6px space-y-1.5 gap). Null when nothing is being dragged.
  const listRef = useRef(null)
  const [drag, setDrag] = useState(null)

  // The drop slot comes from the pointer's position inside the LIST, never from whichever
  // card is under the cursor: the cards are mid-translate, so hit-testing them would flip
  // the target back and forth as they slide out from under the pointer.
  function dragOverList(e) {
    if (!drag || !listRef.current) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const top = listRef.current.getBoundingClientRect().top
    const to = Math.max(0, Math.min(rows.length - 1, Math.floor((e.clientY - top) / drag.pitch)))
    if (to !== drag.to) setDrag((d) => (d ? { ...d, to } : d))
  }

  function endDrag(commit) {
    if (drag && commit) move(drag.from, drag.to)
    setDrag(null)
  }

  // Where card j has to sit while the drag is in flight. The dragged card travels the whole
  // distance; everything it passes shifts one slot the other way to open the gap. It is a
  // permutation, so the list keeps its exact height and nothing below it jumps.
  function slotOffset(j) {
    if (!drag || drag.from === drag.to) return 0
    const { from, to, pitch } = drag
    if (j === from) return (to - from) * pitch
    if (from < to && j > from && j <= to) return -pitch
    if (from > to && j >= to && j < from) return pitch
    return 0
  }

  return (
    <Section
      icon={Layers}
      title="รูปแบบการเก็บเงิน (pattern)"
      right={
        <span className="text-[11.5px] text-slate-400 tabular-nums">
          {max} ครั้ง · ฿{baht(total)}
        </span>
      }
    >
      <p className="text-[12px] text-slate-400 leading-relaxed -mt-0.5 mb-2">
        เรียงตามลำดับการเก็บเงิน · ครั้งแรกที่คนไข้มาจะถูกคิดตามขั้นที่ 1 แล้วไล่ลงไปเรื่อย ๆ
        {editing && ' · ลากปุ่มซ้ายเพื่อสลับลำดับ'}
      </p>

      {!rows.length && (
        <div className="rounded-lg border border-dashed border-slate-200 px-3 py-5 text-center text-[12.5px] text-slate-400">
          ยังไม่มีขั้นการเก็บเงิน — โปรโมชั่นนี้จะออกบิลไม่ได้
        </div>
      )}

      <div
        ref={listRef}
        className={`space-y-1.5 ${drag ? 'cursor-grabbing select-none' : ''}`}
        onDragOver={dragOverList}
        onDrop={(e) => { e.preventDefault(); endDrag(true) }}
      >
        {rows.map((s, i) => (
          <StepRow
            key={i}
            i={i}
            s={s}
            seq={derived[i]}
            last={i === rows.length - 1}
            editing={editing}
            options={options}
            btById={btById}
            lifted={drag?.from === i}
            offset={slotOffset(i)}
            sliding={!!drag}
            onDragStart={(from, pitch) => setDrag({ from, to: from, pitch })}
            onDragEnd={() => endDrag(false)}
            onMove={move}
            onPatch={patch}
            onRemove={() => onChange(rows.filter((_, j) => j !== i))}
          />
        ))}
      </div>

      {editing && (
        <div className="flex items-center gap-2 pt-1.5">
          <button
            onClick={() => onChange([...rows, emptyStep()])}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px] font-medium text-slate-600 hover:border-brand-300 hover:text-brand-700"
          ><Plus size={13} /> เพิ่มขั้น</button>
          {rows.length > 0 && (
            <button
              onClick={() => onChange([...rows, { ...rows[rows.length - 1] }])}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px] font-medium text-slate-600 hover:border-brand-300 hover:text-brand-700"
            ><Copy size={13} /> ทำซ้ำขั้นสุดท้าย</button>
          )}
        </div>
      )}
    </Section>
  )
}

function StepRow({
  i, s, seq, last, editing, options, btById,
  lifted, offset, sliding, onDragStart, onDragEnd, onMove, onPatch, onRemove,
}) {
  const cardRef = useRef(null)
  const bt = btById.get(Number(s.billing_type_id))
  const mode = dfMode(s)
  const pct = dfPercentHint(s, btById)
  // Printed AS STORED, and as stored is now also as CHARGED: since 2026-09-01 the step
  // price is the price, so a 0 means the patient pays nothing for this installment. It
  // used to fall back to the billing type's own figure at billing time, which is how a
  // package could print ฿12,000 here and take ฿17,000 at the chair (#231).
  // `listPrice` is shown beside a free step so the manager can see what is being given
  // away — it no longer decides anything. See knowledge/xano/promotion-price-zero.md.
  const price = Number(s.price) || 0
  const listPrice = Number(bt?.price) || 0
  const free = price === 0
  const repeat = Number(s.repeat) || 1

  if (!editing) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-slate-50 ring-1 ring-slate-100 px-2.5 py-2 text-[12.5px]">
        <span className="w-5 text-center text-slate-400 tabular-nums shrink-0">{seq.step}</span>
        <span className="min-w-0 flex-1 truncate text-slate-700">
          {btLabel(bt) || <span className="text-rose-500">ไม่พบประเภทบิล #{s.billing_type_id}</span>}
        </span>
        {repeat > 1 && <span className="text-slate-400 tabular-nums shrink-0">×{repeat.toLocaleString()}</span>}
        <span
          className={`w-[86px] text-right tabular-nums shrink-0 ${free ? 'text-emerald-700 font-medium' : 'text-slate-700'}`}
          title={free && listPrice > 0 ? `ฟรี — ไม่เก็บเงินครั้งนี้ (ราคาปกติของรายการนี้ ฿${baht(listPrice)})` : ''}
        >
          {free ? 'ฟรี' : `฿${baht(price)}`}
        </span>
        <span className="w-[92px] text-right tabular-nums shrink-0 text-slate-500">
          {mode === DF_FIXED ? `DF ฿${baht(s.fixedDF)}` : (pct != null ? `DF ${pct}%` : 'DF ตาม %')}
        </span>
        <span className="w-[52px] text-right tabular-nums text-slate-400 shrink-0">/{seq.repeat_acc}</span>
      </div>
    )
  }

  return (
    <div
      ref={cardRef}
      style={{ transform: offset ? `translate3d(0, ${offset}px, 0)` : undefined }}
      // The slide is only animated DURING a drag. On drop the transform drops to 0 in the
      // same commit that reorders the rows — with a transition still attached, every card
      // would slide back out of its brand-new position.
      className={`rounded-lg border bg-white p-2 ${
        sliding ? 'transition-transform duration-200 ease-out' : 'transition-colors'
      } ${lifted
        ? 'border-dashed border-brand-400 bg-brand-50/50 opacity-50'
        : 'border-slate-200'}`}
    >
      <div className="flex items-center gap-1.5">
        <span
          draggable
          onDragStart={(e) => {
            const card = cardRef.current
            if (!card) return
            const box = card.getBoundingClientRect()
            e.dataTransfer.effectAllowed = 'move'
            e.dataTransfer.setData('text/plain', String(i))
            // Ghost the whole card under the cursor. Left to itself the browser would drag a
            // snapshot of the 14px grip icon, which is nothing to aim with.
            e.dataTransfer.setDragImage(card, e.clientX - box.left, e.clientY - box.top)
            // 6px = the space-y-1.5 gap, so pitch is one whole slot.
            onDragStart(i, box.height + 6)
          }}
          onDragEnd={onDragEnd}
          title="ลากเพื่อจัดลำดับ"
          className="cursor-grab active:cursor-grabbing rounded p-1 text-slate-300 shrink-0 transition-colors hover:bg-slate-100 hover:text-brand-600"
        ><GripVertical size={14} /></span>
        <span className="w-5 text-center text-[12px] font-semibold text-slate-500 tabular-nums shrink-0">{seq.step}</span>
        <div className="min-w-0 flex-1">
          <SearchableSelect
            options={options}
            value={s.billing_type_id || ''}
            onChange={(v) => onPatch(i, 'billing_type_id', Number(v) || 0)}
            placeholder="เลือกประเภทบิล…"
            searchPlaceholder="ค้นหาชื่อรายการ หรือ ประเภท…"
          />
        </div>
        <div className="flex flex-col shrink-0">
          <button onClick={() => onMove(i, i - 1)} disabled={i === 0} title="เลื่อนขึ้น" className="text-slate-300 hover:text-brand-600 disabled:opacity-30 leading-none"><ChevronUp size={13} /></button>
          <button onClick={() => onMove(i, i + 1)} disabled={last} title="เลื่อนลง" className="text-slate-300 hover:text-brand-600 disabled:opacity-30 leading-none"><ChevronDown size={13} /></button>
        </div>
        <button onClick={onRemove} title="ลบขั้นนี้" className="p-1.5 text-slate-400 hover:text-rose-600 shrink-0"><Trash2 size={13} /></button>
      </div>

      {/* 4 columns with DF spanning 2: at a third of the row the DF cell left ~60px beside
          its mode select, and "ตามอัตราหมอ" wrapped onto three lines, stretching the whole
          step row. Half the row keeps it on one line. */}
      <div className="grid grid-cols-4 gap-2 mt-1.5 pl-[38px]">
        <label className="space-y-0.5">
          <span className="block text-[11px] text-slate-400">จำนวนครั้ง</span>
          <input
            type="number" min="1" step="1" className={NUM}
            value={s.repeat}
            onChange={(e) => onPatch(i, 'repeat', e.target.value)}
          />
        </label>
        <label className="space-y-0.5">
          <span className="block text-[11px] text-slate-400">ราคา/ครั้ง</span>
          <input
            type="number" min="0" step="1" className={NUM}
            value={s.price}
            onChange={(e) => onPatch(i, 'price', e.target.value)}
            placeholder="0"
          />
        </label>
        <div className="col-span-2 space-y-0.5">
          <span className="block text-[11px] text-slate-400">ค่ามือหมอ (DF)</span>
          <div className="flex items-stretch gap-1">
            {/* null vs 0 is a real distinction: null = pay the doctor their percentage,
                0 = this step pays the doctor nothing. A single number box could not
                express the first, so the mode is its own control. */}
            <select
              className={`${INPUT} !w-[104px] shrink-0 !px-2`}
              value={mode}
              onChange={(e) => onPatch(i, 'fixedDF', e.target.value === DF_PERCENT ? null : 0)}
            >
              <option value={DF_PERCENT}>ตาม %</option>
              <option value={DF_FIXED}>กำหนดเอง</option>
            </select>
            {mode === DF_FIXED ? (
              <input
                type="number" min="0" step="1" className={NUM}
                value={s.fixedDF ?? 0}
                onChange={(e) => onPatch(i, 'fixedDF', e.target.value)}
              />
            ) : (
              <div className="flex-1 min-w-0 flex items-center justify-end rounded-lg bg-slate-50 px-2 text-[12px] text-slate-400 tabular-nums whitespace-nowrap truncate">
                {pct != null ? `${pct}%` : 'ตามอัตราหมอ'}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="pl-[38px] pt-1 text-[11px] text-slate-400 tabular-nums">
        สะสมถึงครั้งที่ {seq.repeat_acc}
        {free && (
          <span className="text-emerald-700"> · ราคา 0 = ฟรี ไม่เก็บเงินครั้งนี้
            {listPrice > 0 && ` (ราคาปกติ ฿${baht(listPrice)})`}
          </span>
        )}
      </div>
    </div>
  )
}

/* ── small shared bits (same shapes as DentistDrawer) ──────────────────────── */

function Section({ icon: Icon, title, right, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-1.5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-[12px] font-medium uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
          <Icon size={12} /> {title}
        </div>
        {right}
      </div>
      {children}
    </section>
  )
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <div className="text-[12px] text-slate-400">{label}{hint && <span className="ml-1 text-slate-300">· {hint}</span>}</div>
      {children}
    </div>
  )
}
const Row2 = ({ children }) => <div className="grid grid-cols-2 gap-2">{children}</div>

function Line({ k, children }) {
  return (
    <div className="flex gap-2 text-[13px]">
      <span className="text-slate-400 shrink-0 w-[112px]">{k}</span>
      <span className="text-slate-700 min-w-0 break-words">{children}</span>
    </div>
  )
}

function Check2({ on, onToggle, label, hint }) {
  return (
    <button onClick={onToggle} className="flex items-start gap-2 text-left w-full py-0.5 group">
      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 mt-0.5 transition ${
        on ? 'bg-brand-600 border-brand-600 text-white' : 'border-slate-300 group-hover:border-brand-400'}`}>
        {on && <Check size={11} strokeWidth={3} />}
      </span>
      <span className="min-w-0">
        <span className="text-[13px] text-slate-700">{label}</span>
        {hint && <span className="block text-[11.5px] text-slate-400 leading-snug">{hint}</span>}
      </span>
    </button>
  )
}
